import React, { useEffect, useState } from "react";
import styled from "styled-components";
import SideBarButton from "./SideBarNavLinks";
import GridViewIcon from "@mui/icons-material/GridView";
import PeopleIcon from "@mui/icons-material/People";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import BarChartIcon from "@mui/icons-material/BarChart";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import CategoryIcon from "@mui/icons-material/Category";
import Logo from "./Logo.js";
import Logout from "./Logout";
import { Scrollbars } from "react-custom-scrollbars";
import { CircularProgress, Box } from "@mui/material"; // import MUI spinner
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { MdUploadFile } from "react-icons/md";
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';


// 🌞 Sidebar Wrapper - Light Theme with Gradient Border
const SideBarWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 260px;
  height: 100vh;
  z-index: 2;
  background: #ffffff;
  border-right: 3px solid transparent;
  background-image: linear-gradient(#ffffff, #ffffff),
    linear-gradient(180deg, #3b82f6, #06b6d4, #f43f5e);
  background-origin: border-box;
  background-clip: padding-box, border-box;
  box-shadow: 4px 0 15px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease-in-out;
`;

// ✨ Sidebar Body with Soft Gradient
const SideBarBody = styled.div`
  background: linear-gradient(145deg, #f9fafb 0%, #ffffff 100%);
  height: 100vh;
  display: flex;
  flex-direction: column;
`;

// 🔹 UL Reset
const UnorderList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

// 🔹 Styled Button Wrapper for Modern Look
export const NavButton = styled.li`
  display: flex;
  align-items: center;
  padding: 12px 20px;
  font-size: 14px;
  color: ${({ active }) => (active ? "#1e3a8a" : "#374151")};
  font-weight: ${({ active }) => (active ? "600" : "500")};
  background: ${({ active }) =>
    active
      ? "linear-gradient(90deg, #e0f2fe, #dbeafe)"
      : "transparent"};
  border-radius: 8px;
  margin: 4px 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    margin-right: 10px;
    font-size: 18px;
    color: ${({ active }) => (active ? "#2563eb" : "#6b7280")};
    transition: color 0.2s ease;
  }

  &:hover {
    background: ${({ active }) =>
      active
        ? "linear-gradient(90deg, #dbeafe, #bfdbfe)"
        : "#f3f4f6"};
    color: #1e3a8a;

    svg {
      color: #2563eb;
    }
  }
`;


// 🔹 Menu Data
const makeButtons = [
  { to: "/home", icon: <BarChartIcon />, title: "Dashboard", roles: ["admin", "employee"] },
  { to: "/addcategory", icon: <GridViewIcon />, title: "Add Category", roles: ["admin", "employee"] },
  {
    to: "/items",
    title: "Items",
    icon: <StorefrontIcon />,
    roles: ["admin", "employee"],
    subBtn: [
      { title: "Item List", icon: <ShoppingCartIcon />, path: "/items", roles: ["admin", "employee"] },
      { title: "Item Actions", icon: <MdUploadFile />, path: "/itemactions", roles: ["admin"] },
      { title: "Offer Items", icon: <WorkspacePremiumIcon />, path: "/offeritemsall", roles: ["admin", "employee"] },
    ],
  },
  // {
  //   to: "/employeemaster",
  //   title: "Employee Master",
  //   icon: <PeopleIcon />,
  //   roles: ["admin"],
  //   subBtn: [
  //     { title: "Employee List", icon: <FaUserTie />, path: "/employeemaster", roles: ["admin"] },
  //   ],
  // },

  // Employee master
  { to: "/employeemaster", icon: <PeopleIcon />, title: "Employee Master", roles: ["admin"] },

  { to: "/orderdetails", icon: <MoveToInboxIcon />, title: "Orders", roles: ["admin", "employee"] },
  
  // Services
  { to: "/servicelist", icon: <BuildCircleIcon />, title: "Service List", roles: ["admin", "employee"] },

  // Offers
  { to: "/offerbannerlists", icon: <LocalOfferIcon />, title: "Offers", roles: ["admin", "employee"] },

  { to: "/notifications", icon: <NotificationsActiveIcon />, title: "Notifications", roles: ["admin"] },

  // // Staff only 
  // { to: "/staffscreen", icon: <LocalOfferIcon />, title: "Staff", roles: ["admin", "employee"] },

  // {
  //   to: "/servicelist",
  //   icon: <FaBox />,
  //   title: "Services",
  //   roles: ["admin", "employee"],
  //   subBtn: [
  //     {
  //       title: "Service List",
  //       icon: <BuildCircleIcon />,
  //       path: "/servicelist",
  //       roles: ["admin", "employee"],
  //     },
  //   ],
  // },
];

const Sidebar = () => {
  const [userRole, setUserRole] = useState(localStorage.getItem("userRole"));
  const [loading, setLoading] = useState(true); // ✅ loading state

  useEffect(() => {
    // Simulate async load or fetch user role (can replace with real API if needed)
    const timeout = setTimeout(() => setLoading(false), 300); // small delay for demo

    // Handle cross-tab changes
    const handleStorageChange = () => {
      setUserRole(localStorage.getItem("userRole"));
    };
    window.addEventListener("storage", handleStorageChange);

    // Handle same-tab updates
    const interval = setInterval(() => {
      const currentRole = localStorage.getItem("userRole");
      setUserRole((prev) => (prev !== currentRole ? currentRole : prev));
    }, 500);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Filter buttons based on role
  const filteredButtons = makeButtons
    .map((btn) => {
      const subBtns =
        Array.isArray(btn.subBtn) && btn.subBtn.length > 0
          ? btn.subBtn.filter((sub) => sub.roles.includes(userRole))
          : null;
      return { ...btn, subBtn: subBtns?.length ? subBtns : null };
    })
    .filter((btn) => {
      if (!btn.roles.includes(userRole)) return false;
      if (btn.subBtn === null && btn.roles.includes(userRole)) return true;
      if (btn.subBtn && btn.subBtn.length > 0) return true;
      return false;
    });

  // 🔹 Show loading spinner until sidebar is ready
  if (loading) {
    return (
      <SideBarWrapper>
        <SideBarBody>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress size={30} />
          </Box>
        </SideBarBody>
      </SideBarWrapper>
    );
  }

  // 🔹 Render normal sidebar
  return (
    <SideBarWrapper>
      <SideBarBody>
        <Logo />
        <Scrollbars
          autoHide
          autoHideTimeout={800}
          autoHideDuration={200}
          style={{ height: "calc(100vh - 180px)" }}
        >
          <UnorderList>
            {filteredButtons.map((btn, index) => (
              <SideBarButton
                key={index}
                to={btn.to}
                icon={btn.icon}
                title={btn.title}
                subBtn={btn.subBtn}
              />
            ))}
          </UnorderList>
        </Scrollbars>
        <Logout />
      </SideBarBody>
    </SideBarWrapper>
  );
};

export default Sidebar;
